import { forwardRef } from '@heroui/system';
import { extendVariants, Button, ButtonProps } from '@heroui/react';

const IconButton = extendVariants(Button, {
  variants: {
    variant: {
      solid:
        'bg-base-5 text-white data-[hover=true]:bg-base-10 data-[hover=true]:opacity-100! data-[pressed=true]:bg-base-10 aria-expanded:opacity-100 shrink-0',
      outline:
        'bg-transparent text-base-5 border border-base-5 border-solid data-[hover=true]:bg-white data-[hover=true]:opacity-100! data-[pressed=true]:bg-white aria-expanded:opacity-100 shrink-0',
      light:
        'bg-transparent text-base-5 data-[hover=true]:bg-white data-[hover=true]:opacity-100! data-[pressed=true]:bg-white aria-expanded:opacity-100 shrink-0',
      filled:
        'bg-base-50 text-base-5 data-[hover=true]:bg-base-80 data-[hover=true]:opacity-100! data-[pressed=true]:bg-base-80 aria-expanded:opacity-100 shrink-0',
      action:
        'bg-transparent text-base-5 data-[hover=true]:opacity-100! data-[pressed=true]:bg-transparent data-[hover=true]:bg-transparent overflow-visible aria-expanded:opacity-100 shrink-0',
    },
    size: {
      xs: 'w-6 h-6 [&>svg]:w-4 [&>svg]:h-4 min-w-0',
      'xs-m': 'w-6 h-6 [&>svg]:w-6 [&>svg]:h-6 min-w-0',
      'xs-s': 'w-6 h-6 [&>svg]:w-5 [&>svg]:h-5 min-w-0',
      sm: 'w-7.5 h-7.5 [&>svg]:w-5 [&>svg]:h-5 min-w-0',
      md: 'w-9 h-9 min-w-0',
      lg: 'w-11 h-11 min-w-0',
    },
    isDisabled: {
      true: 'opacity-40 cursor-not-allowed',
    },
  },
  defaultVariants: {
    size: 'lg',
    variant: 'solid',
    isDisabled: false,
  },
  compoundVariants: [
    {
      class: 'font-semibold',
    },
  ],
});

export interface IconButtonProps extends Omit<ButtonProps, 'variant' | 'size' | 'startContent' | 'ref'> {
  variant?: 'solid' | 'light' | 'outline' | 'filled' | 'action';
  size?: 'xs-m' | 'xs-s' | 'xs' | 'sm' | 'md' | 'lg';
  startContent: React.ReactNode;
}

const IconButtonWrapper = forwardRef<'button', IconButtonProps>((props, ref) => {
  const { startContent, ...restProps } = props;
  return <IconButton ref={ref} isIconOnly radius="full" startContent={startContent} {...restProps} />;
});

IconButtonWrapper.displayName = 'IconButton';

export default IconButtonWrapper;
